import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { RealtimeEnvelope } from '@/contracts/realtime-envelope-v1';
import { notificationsService } from '@/services/erpat';
import { getSessionEpoch } from '@/services/api/erpat-client';
import { compareStreamSequence, mapSnapshotNotifications } from '@/services/api/models';
import type { CanonicalNotification, NotificationState, ReducerOutcome } from '@/types/notification';

/**
 * The authoritative notification state reducer (phase6-contract.md §3.3,
 * TASK-505) and the header bell badge.
 *
 * Per-item state is keyed by the canonical `notification_id` and carries the
 * recipient-state `state_revision` the server stamps on every row, snapshot
 * item and stream event. Three sources feed it and all three converge on the
 * same rule — a transition applies only when its revision is NEWER than what
 * is held:
 *
 *   - the bootstrap snapshot (`applySnapshot`) REPLACES the items it carries;
 *   - stream events (`applyEvent`) apply `created` / `read` / `dismissed`
 *     monotonically (a duplicate, a reorder or a replay is `'stale'`, never a
 *     regression);
 *   - the REST list (`refresh`) re-floors: a row with a numeric revision applies
 *     iff it is ≥ the held one; a row with `null` (a tenant with no
 *     recipient-state rows) is authoritative and REPLACES the state unless the
 *     item is optimistic — the server wins on revision-less tenants (DEC-084;
 *     the old device-memory overlay's one-way rule is gone on purpose).
 *
 * `read` / `dismiss` are optimistic: the item flips at once, the POST follows,
 * a failure rolls the item back. The item stays `optimistic` until a row or an
 * event with a revision at or past the local guess acknowledges it.
 *
 * The badge ({@link unread}) is the server's count adjusted by newer local
 * transitions (§3.3): `serverUnread` (from `applyUnreadCounts` on a snapshot /
 * drain page, or the list envelope's `unread_count` on refresh) minus the
 * items that count counted as unread and are now read / dismissed, plus the
 * items created after that count and still unread. Each held item remembers
 * the state the SERVER last asserted for it (`serverRead` / `serverDismissed`
 * — from a row, a snapshot item or an event) beside the projected local
 * state; when a count lands, "counted as unread" is derived from that server
 * state, so an optimistic read the server has not processed yet is subtracted
 * exactly once and never twice.
 *
 * Errors are swallowed on purpose — a badge must never break the header on a
 * page whose own data loaded fine. A failed fetch leaves the last known value.
 */
const STALE_MS = 60_000;
const DECIMAL_ID_RE = /^[0-9]{1,20}$/;

interface HeldItem extends NotificationState {
  /** The read state the server last asserted (row / snapshot / event). */
  serverRead: boolean;
  /** The dismissed state the server last asserted. */
  serverDismissed: boolean;
  /**
   * `true` when `serverUnread` (as of `serverAsOf`) counted this item as
   * unread — derived from the server-asserted state when the count landed,
   * `false` for an item created after that count.
   */
  counted: boolean;
}

/** The two envelope types that create a bell item (a recipient's `message.created` carries one too). */
const CREATE_TYPES: ReadonlySet<string> = new Set(['notification.created', 'message.created']);

function cloneItem(item: HeldItem): HeldItem {
  return { ...item };
}

export const useNotificationsStore = defineStore('notifications', () => {
  /** Per-item state keyed by the canonical decimal `notification_id`. */
  const items = ref(new Map<string, HeldItem>());
  /** The server's unread total as of {@link serverAsOf}. */
  const serverUnread = ref(0);
  /** The stream sequence {@link serverUnread} is consistent with; `null` before any count. */
  const serverAsOf = ref<string | null>(null);

  let fetchedAt = 0;
  let inflight: Promise<void> | null = null;
  // Bumped by reset(). A run captures it and drops its result if it no longer
  // matches, so a fetch abandoned by a sign-out can't land afterwards.
  let generation = 0;
  // The highest stream sequence this store has applied (events + snapshots):
  // a count or a snapshot older than it is stale and ignored.
  let highestSequence: string | null = null;

  /** The header badge: the server's count adjusted by local transitions newer than it (§3.3). */
  const unread = computed(() => {
    let total = serverUnread.value;
    for (const item of items.value.values()) {
      const unreadNow = !item.read && !item.dismissed;
      if (item.counted && !unreadNow) total -= 1;
      else if (!item.counted && unreadNow) total += 1;
    }
    return total > 0 ? total : 0;
  });

  function advanceSequence(sequence: string | null | undefined): void {
    if (typeof sequence !== 'string' || !DECIMAL_ID_RE.test(sequence)) return;
    if (highestSequence === null || compareStreamSequence(sequence, highestSequence) > 0) {
      highestSequence = sequence;
    }
  }

  /** Whether a count / snapshot consistent as of `asOf` is at least as new as everything applied. */
  function isCurrent(asOf: string | null): boolean {
    if (asOf === null) return true;
    if (!DECIMAL_ID_RE.test(asOf)) return false;
    if (highestSequence !== null && compareStreamSequence(asOf, highestSequence) < 0) return false;
    if (serverAsOf.value !== null && compareStreamSequence(asOf, serverAsOf.value) < 0) return false;
    return true;
  }

  /** The held state of one item, or `undefined` when only the REST row knows it. */
  function stateOf(notificationId: string): NotificationState | undefined {
    return items.value.get(notificationId);
  }

  /**
   * Adopt the server's unread total. `asOf` is the stream sequence the count is
   * consistent with (a snapshot's `as_of_sequence`, a changes page's
   * `counts_as_of_sequence`); `null` means "now" (the REST list). A count older
   * than what this store has already applied is ignored — it cannot describe
   * the newer state.
   */
  function setServerUnread(count: number, asOf: string | null): void {
    if (!isCurrent(asOf)) return;
    serverUnread.value = Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
    serverAsOf.value = asOf ?? highestSequence ?? serverAsOf.value;
    if (asOf !== null) advanceSequence(asOf);
    // The count is the server's view, and every held item was created at or
    // before it (an older count is refused above): each is counted per the
    // state the server last asserted for it — not the local projection, which
    // may run ahead through an optimistic read the server has yet to process.
    for (const item of items.value.values()) {
      item.counted = !item.serverRead && !item.serverDismissed;
    }
    fetchedAt = Date.now();
  }

  /**
   * The bootstrap snapshot: REPLACE the items it carries (only when it is at
   * least as new as everything applied). No synthesized-revision rule — the
   * client cannot tell a synthesized `1`/`2` from a real one, and the snapshot
   * is consistent as of `asOf` by construction.
   */
  function applySnapshot(rows: unknown[], asOf: string): boolean {
    if (!isCurrent(asOf)) return false;
    const canonical = mapSnapshotNotifications(rows);
    for (const row of canonical) {
      items.value.set(row.notificationId, {
        notificationId: row.notificationId,
        read: row.read,
        dismissed: row.dismissed,
        stateRevision: row.stateRevision,
        optimistic: false,
        serverRead: row.read,
        serverDismissed: row.dismissed,
        // The count that accompanies a snapshot is as of the same sequence.
        counted: !row.read && !row.dismissed,
      });
    }
    advanceSequence(asOf);
    return true;
  }

  /**
   * Reduce one accepted stream envelope (live or drained). Returns what
   * happened so the realtime service can count `'stale'` and refresh once per
   * burst on `'unresolved'` (never here — a notification event needs no
   * lookup). Every envelope advances the applied sequence, whatever the outcome.
   */
  function applyEvent(envelope: RealtimeEnvelope): ReducerOutcome {
    advanceSequence(envelope.sequence);
    const notification = envelope.notification;
    if (!notification) return 'ignored';
    const id = notification.id;
    // A non-decimal id (the gateway's UUID test event) is never stored.
    if (!DECIMAL_ID_RE.test(id)) return 'ignored';
    const held = items.value.get(id);

    if (CREATE_TYPES.has(envelope.type)) {
      if (held) {
        // A `created` after a drained `read` (any held revision ≥ 1) is a no-op:
        // the held state is newer by definition.
        if (held.stateRevision !== null && held.stateRevision >= 1) return 'stale';
        // A revision-less row (REST on a revision-less tenant) learns the
        // event's revision; its read state came from the server and stands —
        // a `created` carries no read state, so it cannot be newer.
        held.stateRevision = envelope.state_revision;
        return 'applied';
      }
      items.value.set(id, {
        notificationId: id,
        read: false,
        dismissed: false,
        stateRevision: envelope.state_revision,
        optimistic: false,
        serverRead: false,
        serverDismissed: false,
        // Created at or before the server's count → that count includes it;
        // created after it → the badge adds it until the next count.
        counted: serverAsOf.value !== null && compareStreamSequence(envelope.sequence, serverAsOf.value) <= 0,
      });
      return 'applied';
    }

    if (envelope.type === 'notification.read' || envelope.type === 'notification.dismissed') {
      const revision = envelope.state_revision;
      const isRead = envelope.type === 'notification.read';
      if (held) {
        const floor = held.stateRevision;
        // A null local floor accepts any numeric revision; an optimistic item
        // is acknowledged by a revision at or past its local guess (that IS
        // our own transition echoed back through the stream).
        const newer = floor === null || revision > floor || (held.optimistic && revision >= floor);
        if (!newer) return 'stale';
        held.stateRevision = revision;
        held.optimistic = false;
        if (isRead) {
          held.read = true;
          held.serverRead = true;
        } else {
          held.dismissed = true;
          held.serverDismissed = true;
        }
        return 'applied';
      }
      items.value.set(id, {
        notificationId: id,
        read: isRead,
        dismissed: !isRead,
        stateRevision: revision,
        optimistic: false,
        serverRead: isRead,
        serverDismissed: !isRead,
        // The server records a read only for a REAL unread → read change, so a
        // read newer than the count means the count still held it as unread.
        // A dismissal says nothing about the prior read state: never subtract.
        counted:
          isRead &&
          serverAsOf.value !== null &&
          compareStreamSequence(envelope.sequence, serverAsOf.value) > 0,
      });
      return 'applied';
    }

    // `message.read` is parse-valid but has no emitter (the server records a
    // message read as a `notification.read` of category `message`).
    return 'ignored';
  }

  /**
   * Re-floor one held item from a REST row, or hold the row when it is new.
   * Numeric revision: applies iff ≥ local. `null` revision: authoritative —
   * replaces read / dismissed unless the item is optimistic (and clears the
   * optimistic flag once the row agrees with the local state).
   */
  function reconcileRow(row: CanonicalNotification): void {
    const held = items.value.get(row.notificationId);
    if (!held) {
      items.value.set(row.notificationId, {
        notificationId: row.notificationId,
        read: row.read,
        dismissed: row.dismissed,
        stateRevision: row.stateRevision,
        optimistic: false,
        serverRead: row.read,
        serverDismissed: row.dismissed,
        counted: !row.read && !row.dismissed,
      });
      return;
    }
    // Whatever its revision, the row IS what the server asserts right now.
    held.serverRead = row.read;
    held.serverDismissed = row.dismissed;
    if (row.stateRevision !== null) {
      if (held.stateRevision !== null && row.stateRevision < held.stateRevision) return;
      held.stateRevision = row.stateRevision;
      held.read = row.read;
      held.dismissed = row.dismissed;
      held.optimistic = false;
      return;
    }
    // Revision-less tenant: the server's row wins outright, except over an
    // in-flight local transition the server has not caught up with.
    if (held.optimistic) {
      if (held.read === row.read && held.dismissed === row.dismissed) held.optimistic = false;
      return;
    }
    held.read = row.read;
    held.dismissed = row.dismissed;
  }

  /**
   * Re-sync from the list: the rows re-floor the held items and the envelope's
   * live `unread_count` becomes the badge's server term. Concurrent calls share
   * one request.
   */
  function refresh(): Promise<void> {
    if (inflight) return inflight;

    // Capture the session generation before awaiting: a sign-out or expiry that
    // races this request must not repopulate the badge onto the next (or guest)
    // session, and a quick login to another account must not have its newer
    // count overwritten by the previous account's reply. Same guard the user and
    // approvals stores use.
    const epoch = getSessionEpoch();
    const gen = generation;

    const run = notificationsService
      .unreadCount()
      .then((list) => {
        // Teardown wins: drop a result that resolved after the session changed
        // or after a reset() superseded this run.
        if (getSessionEpoch() !== epoch || gen !== generation) return;
        for (const row of list.notifications) reconcileRow(row);
        setServerUnread(list.unreadCount, null);
      })
      .catch(() => {
        // Keep the last known value; never surface a badge failure.
      })
      .finally(() => {
        // Only clear the slot if it is still THIS run's — a reset() followed by
        // a new refresh() must not have its request nulled out by this one.
        if (inflight === run) inflight = null;
      });
    inflight = run;
    return run;
  }

  /** Refresh only if the count was never loaded or has gone stale. */
  function refreshIfStale(maxAgeMs = STALE_MS): void {
    if (Date.now() - fetchedAt < maxAgeMs) return;
    void refresh();
  }

  /** Snapshot the items about to change so a failed POST can put them back. */
  function snapshotFor(ids: readonly string[]): Map<string, HeldItem | null> {
    const before = new Map<string, HeldItem | null>();
    for (const id of ids) {
      const held = items.value.get(id);
      before.set(id, held ? cloneItem(held) : null);
    }
    return before;
  }

  function rollback(before: Map<string, HeldItem | null>): void {
    for (const [id, previous] of before) {
      if (previous) items.value.set(id, previous);
      else items.value.delete(id);
    }
  }

  /**
   * Mark notifications read — optimistic, then the POST, then a rollback on
   * failure. `all: true` is the inbox's "mark all read": one `read-all` request
   * (the server marks every page, not only the ids on screen) and the badge's
   * server term drops to zero with it. Resolves `true` when the server accepted.
   */
  async function markRead(ids: string[], options: { all?: boolean } = {}): Promise<boolean> {
    const targets = ids.filter((id) => DECIMAL_ID_RE.test(id));
    const pending = targets.filter((id) => {
      const held = items.value.get(id);
      return !held || (!held.read && !held.dismissed);
    });
    if (!pending.length && !options.all) return true;

    const before = snapshotFor(pending);
    const unreadBefore = serverUnread.value;
    for (const id of pending) {
      const held = items.value.get(id);
      if (held) {
        held.read = true;
        held.optimistic = true;
        held.stateRevision = (held.stateRevision ?? 0) + 1;
      } else {
        // Only the REST row knew it, as unread — so the server's count held it.
        items.value.set(id, {
          notificationId: id,
          read: true,
          dismissed: false,
          stateRevision: 1,
          optimistic: true,
          serverRead: false,
          serverDismissed: false,
          counted: true,
        });
      }
    }
    if (options.all) {
      // Everything the server has for this user is about to be read, including
      // the pages the inbox never loaded: nothing is left for the count to hold.
      serverUnread.value = 0;
      for (const item of items.value.values()) item.counted = false;
    }

    const epoch = getSessionEpoch();
    const gen = generation;
    try {
      if (options.all) {
        await notificationsService.readAll();
      } else {
        await Promise.all(pending.map((id) => notificationsService.read(id)));
      }
      return true;
    } catch {
      if (getSessionEpoch() === epoch && gen === generation) {
        rollback(before);
        if (options.all) serverUnread.value = unreadBefore;
      }
      return false;
    }
  }

  /** Dismiss one notification — optimistic, POST, rollback on failure. */
  async function dismiss(id: string): Promise<boolean> {
    if (!DECIMAL_ID_RE.test(id)) return false;
    const held = items.value.get(id);
    if (held?.dismissed) return true;

    const before = snapshotFor([id]);
    if (held) {
      held.dismissed = true;
      held.optimistic = true;
      held.stateRevision = (held.stateRevision ?? 0) + 1;
    } else {
      // The prior read state is unknown here, so the badge is not adjusted
      // (`counted: false`); the next count settles it.
      items.value.set(id, {
        notificationId: id,
        read: false,
        dismissed: true,
        stateRevision: 1,
        optimistic: true,
        serverRead: false,
        serverDismissed: false,
        counted: false,
      });
    }

    const epoch = getSessionEpoch();
    const gen = generation;
    try {
      await notificationsService.dismiss(id);
      return true;
    } catch {
      if (getSessionEpoch() === epoch && gen === generation) rollback(before);
      return false;
    }
  }

  /**
   * @deprecated Adopt a bare server count with no sequence — kept for the
   * pre-Phase-6 call sites; new code passes the as-of sequence to
   * {@link setServerUnread}.
   */
  function setUnread(count: number): void {
    setServerUnread(count, null);
  }

  /** @deprecated Drop the server term by `by`; the inbox now goes through {@link markRead}. */
  function decrement(by = 1): void {
    serverUnread.value = Math.max(0, serverUnread.value - by);
    fetchedAt = Date.now();
  }

  /** Clear all state — call on sign-out so the next user starts clean. */
  function reset(): void {
    generation += 1;
    items.value = new Map();
    serverUnread.value = 0;
    serverAsOf.value = null;
    highestSequence = null;
    fetchedAt = 0;
    inflight = null;
  }

  return {
    items,
    serverUnread,
    serverAsOf,
    unread,
    stateOf,
    setServerUnread,
    applySnapshot,
    applyEvent,
    markRead,
    dismiss,
    refresh,
    refreshIfStale,
    setUnread,
    decrement,
    reset,
  };
});
